import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Plus,
  Search,
  Download,
  Clock,
  CheckCircle,
  XCircle,
  MoreVertical,
  Printer,
  Eye,
  Edit,
  Trash2,
  PlayCircle,
  ArrowRight,
  Upload,
  Star
} from "lucide-react";

const LocalServiceOrders = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [selectedLSO, setSelectedLSO] = useState<any>(null);

  // Mock LSO data
  const lsoList = [
    {
      id: "LSO-2024-001",
      date: "2024-03-05",
      serviceProvider: "Elite Painting Services",
      serviceType: "Painting & Decoration",
      description: "Complete repainting of classroom block A - exterior walls, windows, and doors",
      estimatedCost: 85000,
      actualCost: 82000,
      startDate: "2024-03-10",
      completionDate: "2024-03-20",
      expectedCompletion: "2024-03-20",
      status: "approved",
      serviceStatus: "completed",
      department: "Maintenance",
      quotationRef: "QT-2024-PAINT-001",
      createdBy: "Maintenance Supervisor",
      completionReport: "completion_report.pdf",
      photos: ["before.jpg", "after.jpg"],
      rating: 5,
      convertedToPV: true,
      pvNumber: "PV-2024-010"
    },
    {
      id: "LSO-2024-002",
      date: "2024-03-12",
      serviceProvider: "QuickFix Plumbing Solutions",
      serviceType: "Plumbing Repairs",
      description: "Fix water leakages in dormitory bathrooms and replace worn-out pipes",
      estimatedCost: 45000,
      actualCost: null,
      startDate: "2024-03-18",
      completionDate: null,
      expectedCompletion: "2024-03-22",
      status: "approved",
      serviceStatus: "in_progress",
      department: "Boarding",
      quotationRef: "QT-2024-PLUMB-012",
      createdBy: "Boarding Master",
      completionReport: null,
      photos: [],
      rating: null,
      convertedToPV: false,
      pvNumber: null
    },
    {
      id: "LSO-2024-003",
      date: "2024-03-16",
      serviceProvider: "Green Cleaners Ltd",
      serviceType: "Deep Cleaning",
      description: "Deep cleaning and fumigation of dining hall and kitchen area",
      estimatedCost: 25000,
      actualCost: null,
      startDate: "2024-03-25",
      completionDate: null,
      expectedCompletion: "2024-03-26",
      status: "pending",
      serviceStatus: "not_started",
      department: "Kitchen",
      quotationRef: "QT-2024-CLEAN-005",
      createdBy: "Kitchen Supervisor",
      completionReport: null,
      photos: [],
      rating: null,
      convertedToPV: false,
      pvNumber: null
    }
  ];

  const serviceCategories = [
    "Repairs & Maintenance",
    "Painting & Decoration",
    "Plumbing Services",
    "Electrical Work",
    "Cleaning Services",
    "Carpentry",
    "Landscaping & Gardening",
    "Transport Services",
    "Catering Services",
    "Security Services",
    "IT Support",
    "Other"
  ];

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400">
          <CheckCircle className="h-3 w-3 mr-1" />
          Approved
        </Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400">
          <Clock className="h-3 w-3 mr-1" />
          Pending
        </Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400">
          <XCircle className="h-3 w-3 mr-1" />
          Rejected
        </Badge>;
      case 'draft':
        return <Badge variant="outline">Draft</Badge>;
      default:
        return <Badge variant="secondary">Unknown</Badge>;
    }
  };

  const getServiceStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400">
          <CheckCircle className="h-3 w-3 mr-1" />
          Completed
        </Badge>;
      case 'in_progress':
        return <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400">
          <PlayCircle className="h-3 w-3 mr-1" />
          In Progress
        </Badge>;
      case 'not_started':
        return <Badge variant="outline">
          <Clock className="h-3 w-3 mr-1" />
          Not Started
        </Badge>;
      default:
        return <Badge variant="secondary">Unknown</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        <div>
          <h2 className="text-2xl font-bold">Local Service Orders (LSOs)</h2>
          <p className="text-sm text-muted-foreground">Manage service contracts and maintenance orders</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Create LSO
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create Local Service Order</DialogTitle>
                <DialogDescription>Issue an order for services and maintenance work</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="lso-date">Date</Label>
                    <Input id="lso-date" type="date" defaultValue={new Date().toISOString().split('T')[0]} />
                  </div>
                  <div>
                    <Label htmlFor="lso-number">LSO Number</Label>
                    <Input id="lso-number" value="LSO-2024-004" disabled />
                  </div>
                </div>

                <div>
                  <Label htmlFor="service-provider">Service Provider/Vendor</Label>
                  <Input id="service-provider" placeholder="Enter service provider name" />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="service-type">Type of Service</Label>
                    <Select>
                      <SelectTrigger>
                        <SelectValue placeholder="Select service type" />
                      </SelectTrigger>
                      <SelectContent>
                        {serviceCategories.map((category) => (
                          <SelectItem key={category} value={category.toLowerCase().replace(/ & /g, '-').replace(/ /g, '-')}>
                            {category}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="department">Department</Label>
                    <Select>
                      <SelectTrigger>
                        <SelectValue placeholder="Select department" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="maintenance">Maintenance</SelectItem>
                        <SelectItem value="boarding">Boarding</SelectItem>
                        <SelectItem value="kitchen">Kitchen</SelectItem>
                        <SelectItem value="admin">Administration</SelectItem>
                        <SelectItem value="academic">Academic</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="service-description">Description of Service</Label>
                  <Textarea 
                    id="service-description" 
                    placeholder="Detailed description of the work to be performed" 
                    rows={4} 
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="start-date">Expected Start Date</Label>
                    <Input id="start-date" type="date" />
                  </div>
                  <div>
                    <Label htmlFor="completion-date">Expected Completion Date</Label>
                    <Input id="completion-date" type="date" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="estimated-cost">Estimated Cost</Label>
                    <Input id="estimated-cost" type="number" placeholder="0.00" />
                  </div>
                  <div>
                    <Label htmlFor="quotation-ref">Quotation Reference</Label>
                    <Input id="quotation-ref" placeholder="QT-2024-XXX-XXX" />
                  </div>
                </div>

                <div>
                  <Label htmlFor="terms">Terms of Service</Label>
                  <Textarea 
                    id="terms" 
                    placeholder="Payment terms, service warranties, guarantees, etc." 
                    rows={3} 
                  />
                </div>

                <div>
                  <Label>Attach Service Contract (Optional)</Label>
                  <div className="border-2 border-dashed rounded-lg p-6 text-center hover:border-primary/50 transition-colors cursor-pointer">
                    <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">Click to upload or drag and drop</p>
                    <p className="text-xs text-muted-foreground mt-1">Service contract, quotation, specifications</p>
                  </div>
                </div>

                <div className="flex gap-2 justify-end pt-4">
                  <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
                  <Button variant="outline">Save as Draft</Button>
                  <Button onClick={() => setShowCreateDialog(false)}>Submit for Approval</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          <Button variant="outline">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by LSO number, service provider, or service type..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={selectedStatus} onValueChange={setSelectedStatus}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* LSO Table */}
      <Card>
        <CardHeader>
          <CardTitle>Service Order Register</CardTitle>
          <CardDescription>All local service orders and their progress</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>LSO Number</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Service Provider</TableHead>
                  <TableHead>Service Type</TableHead>
                  <TableHead>Cost</TableHead>
                  <TableHead>Approval Status</TableHead>
                  <TableHead>Service Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lsoList.map((lso) => (
                  <TableRow key={lso.id}>
                    <TableCell className="font-medium">{lso.id}</TableCell>
                    <TableCell>{lso.date}</TableCell>
                    <TableCell>{lso.serviceProvider}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{lso.serviceType}</Badge>
                    </TableCell>
                    <TableCell className="font-bold">
                      Ksh {(lso.actualCost || lso.estimatedCost).toLocaleString()}
                    </TableCell>
                    <TableCell>{getStatusBadge(lso.status)}</TableCell>
                    <TableCell>{getServiceStatusBadge(lso.serviceStatus)}</TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => {
                            setSelectedLSO(lso);
                            setShowDetailsDialog(true);
                          }}>
                            <Eye className="h-4 w-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem>
                            <Edit className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem>
                            <Printer className="h-4 w-4 mr-2" />
                            Print LSO
                          </DropdownMenuItem>
                          {lso.serviceStatus === 'not_started' && (
                            <DropdownMenuItem>
                              <PlayCircle className="h-4 w-4 mr-2" />
                              Mark as Started
                            </DropdownMenuItem>
                          )}
                          {lso.serviceStatus === 'in_progress' && (
                            <DropdownMenuItem>
                              <CheckCircle className="h-4 w-4 mr-2" />
                              Mark as Completed
                            </DropdownMenuItem>
                          )}
                          {lso.serviceStatus === 'completed' && !lso.convertedToPV && (
                            <DropdownMenuItem>
                              <ArrowRight className="h-4 w-4 mr-2" />
                              Convert to PV
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem className="text-destructive">
                            <Trash2 className="h-4 w-4 mr-2" />
                            Cancel LSO
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Details Dialog */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>LSO Details</DialogTitle>
            <DialogDescription>{selectedLSO?.id}</DialogDescription>
          </DialogHeader>
          {selectedLSO && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Service Provider</Label>
                  <p className="font-medium">{selectedLSO.serviceProvider}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Service Type</Label>
                  <p className="font-medium">{selectedLSO.serviceType}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Start Date</Label>
                  <p className="font-medium">{selectedLSO.startDate}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Expected Completion</Label>
                  <p className="font-medium">{selectedLSO.expectedCompletion}</p>
                </div>
                {selectedLSO.completionDate && (
                  <div>
                    <Label className="text-muted-foreground">Actual Completion</Label>
                    <p className="font-medium">{selectedLSO.completionDate}</p>
                  </div>
                )}
                <div>
                  <Label className="text-muted-foreground">Estimated Cost</Label>
                  <p className="font-medium">Ksh {selectedLSO.estimatedCost.toLocaleString()}</p>
                </div>
                {selectedLSO.actualCost && (
                  <div>
                    <Label className="text-muted-foreground">Actual Cost</Label>
                    <p className="font-bold text-lg">Ksh {selectedLSO.actualCost.toLocaleString()}</p>
                  </div>
                )}
              </div>

              <div>
                <Label className="text-muted-foreground">Service Description</Label>
                <p className="text-sm mt-1 p-3 bg-muted rounded">{selectedLSO.description}</p>
              </div>

              {selectedLSO.serviceStatus === 'completed' && (
                <>
                  {selectedLSO.rating && (
                    <div>
                      <Label className="text-muted-foreground">Service Rating</Label>
                      <div className="flex items-center gap-1 mt-1">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            className={`h-5 w-5 ${
                              i < selectedLSO.rating
                                ? 'fill-yellow-400 text-yellow-400'
                                : 'text-gray-300'
                            }`}
                          />
                        ))}
                        <span className="ml-2 font-medium">{selectedLSO.rating}/5</span>
                      </div>
                    </div>
                  )}

                  {selectedLSO.photos.length > 0 && (
                    <div>
                      <Label className="text-muted-foreground">Photos</Label>
                      <div className="mt-2 flex gap-2">
                        {selectedLSO.photos.map((photo: string, index: number) => (
                          <div key={index} className="h-24 w-24 border rounded flex items-center justify-center bg-muted">
                            <p className="text-xs text-muted-foreground">{photo}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}

              {selectedLSO.convertedToPV && (
                <div className="p-3 bg-green-100 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                  <Label className="font-semibold text-green-800 dark:text-green-400">Converted to Payment Voucher</Label>
                  <p className="text-sm mt-1">This LSO has been converted to {selectedLSO.pvNumber}</p>
                </div>
              )}

              <div className="flex gap-2 justify-end pt-4 border-t">
                <Button variant="outline">
                  <Printer className="h-4 w-4 mr-2" />
                  Print
                </Button>
                {selectedLSO.serviceStatus === 'completed' && !selectedLSO.convertedToPV && (
                  <Button>
                    <ArrowRight className="h-4 w-4 mr-2" />
                    Convert to Payment Voucher
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LocalServiceOrders;